import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Checkbox } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Separator } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { cn } from "@/lib/utils";
import { formatDateBR, formatPetAge, parseISODateOrDateTime } from "@/lib/date-utils";
import {
  Clock3,
  Mail,
  Phone,
  AlertTriangle,
  PlusCircle,
  Trash2,
  User,
  FileText,
  Pencil,
  MapPin,
  Check,
  Play,
  Loader2,
  Building2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { useModuleAccess } from "@/modules/useModuleAccess";
import { useCanStartAttendance } from "@/hooks/useCanStartAttendance";
import { AddressLink } from "@/components/shared/AddressLink";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import type { AppointmentStatus } from "@/hooks/useAppointments";
import type { AppointmentItemRow } from "@/hooks/useAppointmentItems";
import { AppointmentFormBody } from "@/components/calendar/AppointmentForm";
import { AppointmentPackageSection } from "@/components/calendar/AppointmentPackageSection";
import { PrescriptionViewDialog } from "@/components/prescriptions/PrescriptionViewDialog";
import { RecentConsultationsCard } from "@/components/medical-records/RecentConsultationsCard";
import { PermissionGuard } from "@/components/PermissionGuard";
import { AppointmentStatusSidePanel } from "@/components/calendar/AppointmentStatusSidePanel";
import { formatRecurrenceSummary } from "@/lib/recurrence";
import type { PatientDayEventType } from "@/components/calendar/types/patient-day-events";
import { getPatientEventTypeIcon, renderIconBySlug } from "@/lib/event-icons";
import { getItemTypeBadgeClass } from "@/lib/item-type-badge";
import {
  getStatusBadgeStyle,
} from "@/lib/utils/clinic-appointment-status";
import { AppointmentDriverBadge } from "@/components/calendar/AppointmentDriverBadge";
import { getGroomingStatusLabel } from "@/lib/utils/grooming-status";
import {
  isRecord,
  type AppointmentDetailsController,
} from "@/components/calendar/useAppointmentDetailsController";

// ── JSX-only helpers ─────────────────────────────────────────────────────────

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function getOrderItemTypeBadge(kind?: string | null) {
  const baseClass = "h-6 text-xs";
  if (!kind || kind.trim() === "") {
    return (
      <Badge variant="secondary" className={baseClass}>
        Desconhecido
      </Badge>
    );
  }
  const badgeClass = getItemTypeBadgeClass(kind);
  if (badgeClass) {
    return (
      <Badge className={cn(baseClass, badgeClass)}>
        {kind}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className={baseClass}>
      {kind}
    </Badge>
  );
}

function normalizeItemType(value?: string | null) {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "produto" || normalized === "medicamento" || normalized === "product") return "Produto";
  if (normalized === "servico" || normalized === "serviço" || normalized === "service") return "Serviço";
  if (normalized === "kit") return "Kit";
  return value.trim();
}

const DAY_EVENT_LABELS: Record<PatientDayEventType, string> = {
  consultation: "Consulta",
  vaccine: "Vacina",
  exam: "Exame",
  prescription: "Receita",
  grooming: "Banho e tosa",
};

const getDayEventIcon = (type: PatientDayEventType) =>
  getPatientEventTypeIcon(type, { tone: "primary", sizeClass: "h-4 w-4" });

type GroomingStatus = string;

const GROOMING_STATUS_LABELS: Record<GroomingStatus, string> = {
  agendado: "Agendado",
  confirmado: "Confirmado",
  no_banho: "No Banho",
  na_tosa: "Na Tosa",
  secando: "Secando",
  finalizado: "Finalizado",
  entregue: "Entregue",
  pago: "Pago",
  cancelado: "Cancelado",
  faltou: "Faltou",
};

const getUnifiedStatusLabel = (
  status: AppointmentStatus | GroomingStatus,
  statuses?: { name: string; display_name: string }[] | null,
) => {
  const fromDb = statuses?.find((s) => s.name === status)?.display_name;
  if (fromDb) return fromDb;
  if (status in GROOMING_STATUS_LABELS) return GROOMING_STATUS_LABELS[status as GroomingStatus];
  return status;
};

const getUnifiedStatusVariant = (
  status: AppointmentStatus | GroomingStatus,
): "default" | "secondary" | "destructive" | "outline" => {
  if (status === "cancelado" || status === "faltou") return "destructive";
  if (status === "entregue" || status === "finalizado" || status === "pago") return "outline";
  if (status === "agendado") return "secondary";
  return "default";
};

const formatEventDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "-";
  return format(parsed, "dd/MM/yyyy HH:mm", { locale: ptBR });
};

// ── Component ────────────────────────────────────────────────────────────────

export function AppointmentDetailsDesktop({ ctrl }: { ctrl: AppointmentDetailsController }) {
  const { t } = useTranslation(["calendar", "common"]);
  const { has: hasModule } = useModuleAccess();
  const hasFinancial = hasModule("financial");

  const {
    appointment, isLoading, isEditing, open, onOpenChange, appointmentId,
    displayStatus, isGroomingAppointment, appointmentTypeBehavior, groomingStatuses, appointmentStatuses,
    startsAt, endsAt, titleText,
    newBelongingName, setNewBelongingName, handleAddBelonging, addAppointmentBelonging,
    belongingsSummary, isLoadingAppointmentBelongings, visibleBelongings,
    handleToggleBelongingReturned, handleDeleteBelonging,
    incompatibleAppointmentItems, hasActiveSale, itemOptions, searchValue, setSearchValue,
    selectedItemId, setSelectedItemId, availableItemsList, selectedItem,
    quantity, setQuantity, handleAddItem, displayItems, appointmentItems, handleRemoveItem,
    addAppointmentItem, deleteAppointmentItem,
    packageDrafts, activePetId, setActivePetId, handlePackageDraftChange, isPackageSectionLocked,
    paidPackageItems, patientDayEvents, isLoadingPermissions, isLoadingPatientDayEvents,
    handleOpenDayEvent, isOpeningDayEvent,
    sidePanelMode, sidePanelValue, sidePanelStatuses,
    updateAppointmentStatus, statusDraft, setStatusDraft,
    servicesBalanceTotal, packageSuggestedChargeTotal, appointmentBalanceTotal,
    isPaymentSectionSettled, saleByAppointment, canShowPaymentCta, saleHasOutstandingReceivable,
    pets, resolvedProfessional, primaryPhone, primaryEmail, ownerAddressLabel, ownerName,
    activePet, canResumeAttendance, resumePetId, quickStartSource,
    petNotes, petPhoto, pet, isLoadingPetSummary, allergies, temperaments, ownerNotes,
    selectedDayMedicalRecord, isDayMedicalRecordModalOpen, setIsDayMedicalRecordModalOpen, setSelectedDayMedicalRecord,
    selectedDayVaccination, isDayVaccinationModalOpen, setIsDayVaccinationModalOpen,
    selectedDayExam, isDayExamModalOpen, setIsDayExamModalOpen,
    selectedDayGrooming, isDayGroomingModalOpen, setIsDayGroomingModalOpen,
    selectedDayPrescription, isDayPrescriptionModalOpen, setIsDayPrescriptionModalOpen,
    handleCloseEdit, handleEditClick,
    itemScopeDialogOpen, setItemScopeDialogOpen, isApplyingItemScope, setPendingItemAction,
    handleScopeAction, cancelSaleDialogOpen, setCancelSaleDialogOpen,
    isCancellingSale, cancelLinkedSale, handleGoToPayment,
    handleOpenExamFileFromModal,
    isCrossClinicAppointment, crossClinicName, switchClinic,
  } = ctrl;

  const navigate = useNavigate();
  const canStartAttendance = useCanStartAttendance();
  const isStandardOrReturnAppointment =
    appointmentTypeBehavior === "padrao" || appointmentTypeBehavior === "retorno";
  const canOfferStartAttendance =
    isStandardOrReturnAppointment &&
    canStartAttendance &&
    appointment?.status !== "em_atendimento" &&
    appointment?.status !== "cancelado" &&
    appointment?.status !== "finalizado";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent noScrollViewport className="max-h-[90vh] max-w-5xl border border-border/60 bg-white p-0">
          <div className="flex max-h-[90vh] flex-col">
            {/* Header */}
            <div className="shrink-0 border-b border-border/60 bg-white px-4 py-4 sm:px-6">
              <DialogHeader className="text-left">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-2 flex flex-col flex-1 justify-between">
                    <DialogTitle className="break-words text-xl font-semibold leading-tight sm:text-2xl">
                      {titleText}
                    </DialogTitle>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="space-y-2">
                        {appointment ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant="outline"
                              className="capitalize flex items-center gap-1.5 text-white border-0"
                              style={{
                                backgroundColor: appointment.clinic_appointment_type?.color ?? "#6366f1",
                              }}
                            >
                              <div className="flex items-center justify-center">
                                {renderIconBySlug(appointment.clinic_appointment_type?.icon ?? "clipboard-plus", { tone: "white", sizeClass: "h-2.5 w-2.5" })}
                              </div>
                              {appointment.clinic_appointment_type?.name ?? ""}
                            </Badge>
                            <Badge
                              variant={getUnifiedStatusVariant(displayStatus)}
                              className="border-0 capitalize"
                              style={getStatusBadgeStyle(
                                displayStatus,
                                isGroomingAppointment ? groomingStatuses : appointmentStatuses,
                              )}
                            >
                              {getUnifiedStatusLabel(
                                displayStatus,
                                isGroomingAppointment ? groomingStatuses : appointmentStatuses,
                              )}
                            </Badge>
                            <AppointmentDriverBadge
                              isTaxiDog={appointment.is_taxi_dog}
                              driverType={appointment.taxi_dog_type ?? null}
                              iconClassName="h-3.5 w-3.5"
                              className="bg-white text-black border-gray-300 h-4 px-1 text-[9px] font-medium gap-0.5 shrink-0 truncate"
                            />
                          </div>
                        ) : null}
                        {startsAt ? (
                          <DialogDescription className="text-sm text-muted-foreground">
                            {format(startsAt, "PPP", { locale: ptBR })} · {format(startsAt, "HH:mm")} -{" "}
                            {format(endsAt ?? startsAt, "HH:mm")}
                          </DialogDescription>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2 self-end"
                        onClick={handleEditClick}
                        disabled={!appointment || isEditing}
                      >
                        <Pencil className="h-4 w-4" />
                        {t("common:edit")}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogHeader>
            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-8 w-2/3" />
                  <Skeleton className="h-5 w-1/2" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-48 w-full" />
                </div>
              ) : appointment ? (
                <div className="flex flex-col gap-6">
                  <div className="grid gap-6 md:grid-cols-[1fr,420px]">
                    <div className="space-y-6">
                      {appointment.notes ? (
                        <section className="rounded-lg border border-border/60 bg-white p-4">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentDetails.notes")}</p>
                          </div>
                          <div className="mt-3 rounded-md border bg-muted/30 p-3">
                            <p className="whitespace-pre-wrap text-sm text-foreground">
                              {appointment.notes}
                            </p>
                          </div>
                        </section>
                      ) : null}
                      {appointment.recurrence ? (
                        <section className="rounded-lg border border-border/60 bg-white p-4">
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.recurrence")}</p>
                          <p className="mt-2 text-sm text-muted-foreground">
                            {formatRecurrenceSummary({
                              frequency: appointment.recurrence.frequency,
                              interval: appointment.recurrence.interval,
                              endsOn: appointment.recurrence.ends_on ?? null,
                            })}
                          </p>
                        </section>
                      ) : null}
                      {isGroomingAppointment ? (
                        <section className="rounded-lg border border-border/60 bg-white p-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentDetails.groomingChecklist")}</p>
                            {belongingsSummary.total > 0 ? (
                              <Badge
                                variant={belongingsSummary.status === "concluido" ? "outline" : "secondary"}
                                className={cn(
                                  "h-6",
                                  belongingsSummary.status === "concluido"
                                    ? "border-emerald-300 text-emerald-700"
                                    : "border-amber-300 text-amber-700",
                                )}
                              >
                                {belongingsSummary.status === "concluido" ? t("calendar:appointmentDetails.completed") : t("calendar:appointmentDetails.pending")}
                              </Badge>
                            ) : null}
                          </div>

                          {belongingsSummary.total > 0 ? (
                            <p className="mt-2 text-xs text-muted-foreground">
                              {t("calendar:appointmentDetails.returnedSummary", { returned: belongingsSummary.returnedCount, total: belongingsSummary.total })}
                            </p>
                          ) : null}

                          <div className="mt-3 flex gap-2">
                            <Input
                              value={newBelongingName}
                              onChange={(event) => setNewBelongingName(event.target.value)}
                              placeholder={t("calendar:appointmentDetails.belongingPlaceholder")}
                              aria-label={t("calendar:appointmentDetails.belongingName")}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  void handleAddBelonging();
                                }
                              }}
                              disabled={addAppointmentBelonging.isPending || !appointmentId}
                            />
                            <Button
                              type="button"
                              onClick={() => void handleAddBelonging()}
                              disabled={!newBelongingName.trim() || addAppointmentBelonging.isPending || !appointmentId}
                            >
                              {t("calendar:appointmentForm.add")}
                            </Button>
                          </div>

                          <div className="mt-3 space-y-2">
                            {isLoadingAppointmentBelongings && visibleBelongings.length === 0 ? (
                              <>
                                <Skeleton className="h-12 w-full" />
                                <Skeleton className="h-12 w-full" />
                              </>
                            ) : (
                              visibleBelongings.map((belonging) => (
                                <div
                                  key={belonging.id}
                                  className="group flex items-center gap-3 rounded-md border p-3 py-2"
                                >
                                  <Checkbox
                                    id={`belonging-returned-${belonging.id}`}
                                    checked={belonging.returned}
                                    onCheckedChange={(checked) =>
                                      void handleToggleBelongingReturned(belonging.id, Boolean(checked))
                                    }
                                    aria-label={t("calendar:appointmentDetails.markBelongingReturned", { name: belonging.name })}
                                  />
                                  <div className="min-w-0 flex-1 flex items-center gap-2">
                                    <label
                                      htmlFor={`belonging-returned-${belonging.id}`}
                                      className={cn(
                                        "cursor-pointer text-sm font-medium",
                                        belonging.returned && "text-muted-foreground line-through",
                                      )}
                                    >
                                      {belonging.name}
                                    </label>
                                    <span className="text-xs text-muted-foreground">({belonging.returned ? t("calendar:appointmentDetails.returned") : t("calendar:appointmentDetails.received")})</span>
                                  </div>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                                    aria-label={t("calendar:appointmentDetails.deleteBelonging", { name: belonging.name })}
                                    onClick={() => void handleDeleteBelonging(belonging.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              ))
                            )}
                          </div>
                        </section>
                      ) : null}
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.productsAndServices")}</p>
                        </div>
                        <div className="mt-3 space-y-4 text-sm">
                          {incompatibleAppointmentItems.length > 0 ? (
                            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertTitle>{t("calendar:appointmentForm.compatibilityWarningTitle")}</AlertTitle>
                              <AlertDescription>
                                {t("calendar:appointmentForm.compatibilityWarningDescription", {
                                  names: incompatibleAppointmentItems.map((item) => item.title_snapshot).join(", "),
                                })}
                              </AlertDescription>
                            </Alert>
                          ) : null}
                          {!hasActiveSale && (
                            <div className="rounded-lg border border-dashed bg-muted/30 p-4">
                              <div className="grid gap-3 sm:grid-cols-[1.5fr,0.7fr] sm:items-end">
                                <Autocomplete
                                  options={itemOptions}
                                  value={searchValue}
                                  onChange={(v) => {
                                    setSearchValue(v);
                                    if (!v.trim()) setSelectedItemId("");
                                  }}
                                  onSelect={(opt) => {
                                    setSelectedItemId(opt.value);
                                    setSearchValue(opt.label);
                                  }}
                                  selectedValue={selectedItemId}
                                  placeholder={t("calendar:appointmentForm.itemPlaceholder")}
                                  emptyMessage={t("calendar:appointmentForm.noItemResults")}
                                  disabled={availableItemsList.length === 0 || addAppointmentItem.isPending || !appointmentId}
                                  renderOption={(option, isSelected) => {
                                    const item = availableItemsList.find((i) => i.id === option.value);
                                    const typeLabel = item?.type === "PRODUTO" ? t("calendar:appointmentForm.product") : t("calendar:appointmentForm.service");
                                    const badgeClass =
                                      item?.type === "PRODUTO"
                                        ? getItemTypeBadgeClass("PRODUTO")
                                        : getItemTypeBadgeClass("SERVICO");
                                    return (
                                      <>
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 shrink-0",
                                            isSelected ? "opacity-100" : "opacity-0",
                                          )}
                                        />
                                        <span className="min-w-0 flex-1 truncate">{option.label}</span>
                                        <Badge className={cn("h-6 shrink-0 text-xs", badgeClass)}>
                                          {typeLabel}
                                        </Badge>
                                      </>
                                    );
                                  }}
                                />
                                <Input
                                  type="number"
                                  min={1}
                                  value={quantity}
                                  onChange={(event) => {
                                    const value = Number(event.target.value);
                                    setQuantity(Number.isNaN(value) ? 1 : Math.max(1, value));
                                  }}
                                  disabled={addAppointmentItem.isPending || !appointmentId}
                                />
                              </div>
                              <Button
                                type="button"
                                className="mt-3 w-full"
                                onClick={handleAddItem}
                                disabled={!selectedItem || !appointmentId || addAppointmentItem.isPending}
                              >
                                <PlusCircle className="mr-2 h-4 w-4" />
                                {t("calendar:appointmentForm.add")}
                              </Button>
                              {selectedItem && (
                                <p className="mt-3 text-xs text-muted-foreground">
                                  {t("calendar:appointmentForm.unitPrice")}: {formatCurrency(selectedItem.sale_price ?? 0)} · {t("calendar:appointmentForm.estimatedTotal")}: {formatCurrency((selectedItem.sale_price ?? 0) * quantity)}
                                </p>
                              )}
                              {availableItemsList.length === 0 && (
                                <p className="mt-3 text-xs text-muted-foreground">
                                  {t("calendar:appointmentForm.productsAndServices")} no catálogo para adicioná-los aos agendamentos.
                                </p>
                              )}
                            </div>
                          )}

                          {!hasActiveSale && appointmentItems.length > 0 ? <Separator /> : null}

                          <div className="space-y-3">
                            {displayItems.length ? (
                              displayItems.map((row) => {
                                const kind = (() => {
                                  const ref = (row as AppointmentItemRow).item;
                                  if (isRecord(ref) && typeof ref["type"] === "string") {
                                    const value = ref["type"] as string;
                                    if (value === "PRODUTO") return "Produto";
                                    if (value === "SERVICO") return "Serviço";
                                    if (value === "KIT") return "Kit";
                                    return normalizeItemType(value);
                                  }
                                  return null;
                                })();
                                const displayPrice = row._displayPrice;
                                const displayLineTotal = row._displayLineTotal;

                                return (
                                  <div
                                    key={row.id}
                                    className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                                  >
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <p className="min-w-0 flex-1 truncate font-medium">
                                          {row.title_snapshot}
                                        </p>
                                        {getOrderItemTypeBadge(kind)}
                                      </div>
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        {row.qty} × {formatCurrency(displayPrice)} ={" "}
                                        {formatCurrency(displayLineTotal)}
                                      </p>
                                    </div>
                                    {!hasActiveSale && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveItem(row as AppointmentItemRow)}
                                        disabled={deleteAppointmentItem.isPending}
                                        className="self-end sm:self-auto"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                );
                              })
                            ) : null}
                          </div>
                        </div>
                      </section>
                      {!hasActiveSale && (
                        <AppointmentPackageSection
                          clinicId={appointment?.clinic_id ?? ""}
                          petId={activePetId}
                          values={packageDrafts}
                          appointmentType={appointment?.clinic_appointment_type?.behavior ?? "padrao"}
                          disabled={isPackageSectionLocked}
                          isRecurring={Boolean(appointment?.recurrence_id)}
                          readOnly={hasActiveSale}
                          onChange={handlePackageDraftChange}
                        />
                      )}
                      {hasActiveSale && paidPackageItems.length > 0 ? (
                        <section className="rounded-lg border border-border/60 bg-white p-4">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-foreground">{t("calendar:packages.title")}</p>
                          </div>
                          <div className="mt-3 space-y-3">
                            {paidPackageItems.length > 0 ? (
                              paidPackageItems.map((row) => (
                                <div
                                  key={row.id}
                                  className="rounded-md border p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2 h-6">
                                      <p className="min-w-0 flex-1 text-sm font-medium truncate">
                                        {row.title_snapshot}
                                      </p>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                      {row.qty} × {formatCurrency(row._displayPrice)} ={" "}
                                      {formatCurrency(row._displayLineTotal)}
                                    </p>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                {t("calendar:packages.emptySelection")}
                              </p>
                            )}
                          </div>
                        </section>
                      ) : null}
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentDetails.events")}</p>
                        </div>

                        <div className="mt-3 space-y-3">
                          {(isLoadingPermissions || isLoadingPatientDayEvents) ? (
                            <>
                              <Skeleton className="h-16 w-full" />
                              <Skeleton className="h-16 w-full" />
                            </>
                          ) : patientDayEvents.length > 0 ? (
                            patientDayEvents.map((event) => (
                              <button
                                type="button"
                                key={event.id}
                                onClick={() => void handleOpenDayEvent(event)}
                                className="block w-full rounded-lg border bg-muted/20 p-4 text-left transition-colors hover:border-color-dark"
                                disabled={isOpeningDayEvent}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      {getDayEventIcon(event.type)}
                                      <span>{DAY_EVENT_LABELS[event.type]}</span>
                                    </div>
                                    <p className="mt-1 truncate text-sm font-semibold text-foreground">
                                      {event.title}
                                    </p>
                                    {event.subtitle ? (
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        {event.subtitle}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Clock3 className="h-3.5 w-3.5" />
                                    <span>{event.displayTime}</span>
                                  </div>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                              {t("calendar:appointmentDetails.noEventsToday")}
                            </div>
                          )}
                        </div>
                      </section>
                    </div>

                    <div className="space-y-6">
                      <AppointmentStatusSidePanel
                        mode={sidePanelMode}
                        value={sidePanelValue}
                        statuses={sidePanelStatuses}
                        disabled={updateAppointmentStatus.isPending}
                        onChange={(value) => {
                          if (!appointment?.id) return;
                          const nextStatus = value as AppointmentStatus;
                          const previousStatus = statusDraft || appointment.status;
                          setStatusDraft(nextStatus);
                          if (nextStatus === previousStatus) return;
                          updateAppointmentStatus.mutate(
                            { id: appointment.id, status: nextStatus as AppointmentStatus },
                            { onError: () => { setStatusDraft(previousStatus); } },
                          );
                        }}
                      />
                      {hasFinancial ? (
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.payment")}</p>
                          {isPaymentSectionSettled ? (
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                              {t("calendar:financialSettled")}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-3 space-y-3 text-sm">
                          <div className="flex items-center justify-between text-muted-foreground">
                            <span>{t("calendar:appointmentForm.productsAndServicesShort")}</span>
                            <span>{formatCurrency(servicesBalanceTotal)}</span>
                          </div>
                          <div className="flex items-center justify-between text-muted-foreground">
                            <span>{t("calendar:packages.title")}</span>
                            <span>{formatCurrency(packageSuggestedChargeTotal)}</span>
                          </div>
                          <div className="flex items-center justify-between rounded-md bg-muted/60 px-3 py-2 font-semibold text-foreground">
                            <span>{t("calendar:appointmentForm.totalToPay")}</span>
                            <span>{formatCurrency(appointmentBalanceTotal)}</span>
                          </div>
                          {isCrossClinicAppointment && (hasActiveSale || canShowPaymentCta) ? (
                            <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/40 p-3">
                              <p className="text-sm text-muted-foreground">
                                {t("calendar:multiClinic.crossClinicWarning", { name: crossClinicName })}
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-2 w-full"
                                disabled={switchClinic.isPending || !appointment?.clinic_id}
                                onClick={() => {
                                  if (appointment?.clinic_id) switchClinic.mutate(appointment.clinic_id);
                                }}
                              >
                                <Building2 className="h-4 w-4" />
                                {t("calendar:multiClinic.switchToClinic", { name: crossClinicName })}
                              </Button>
                            </div>
                          ) : null}
                          {!isCrossClinicAppointment && hasActiveSale && saleByAppointment ? (
                            <PermissionGuard resource="sales" action="view" showAlert={false}>
                              <Button asChild size="sm" variant="outline" className="mt-3 gap-2 w-full">
                                <Link
                                  to="/sales"
                                  state={{ openSaleId: saleByAppointment.id }}
                                  onClick={() => onOpenChange(false)}
                                >
                                  <FileText className="h-4 w-4" />
                                  {t("calendar:appointmentDetails.viewSale")}
                                </Link>
                              </Button>
                            </PermissionGuard>
                          ) : null}
                          {!isCrossClinicAppointment && !hasActiveSale && canShowPaymentCta ? (
                            <PermissionGuard resource="sales" action="create" showAlert={false}>
                              <Button
                                size="sm"
                                onClick={handleGoToPayment}
                                className="gap-2 w-full"
                              >
                                <FileText className="h-4 w-4" />
                                {t("calendar:appointmentDetails.makePayment")}
                              </Button>
                            </PermissionGuard>
                          ) : null}
                        </div>
                      </section>
                      ) : null}
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <div className="flex items-center gap-2">
                          <User className="h-5 w-5" />
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.responsibleProfessional")}</p>
                        </div>
                        <div className="mt-3 space-y-3 text-sm">
                          {resolvedProfessional?.full_name ? (
                            <>
                              <div className="flex items-center justify-between">
                                <p className="text-base font-semibold text-foreground">
                                  {resolvedProfessional.full_name}
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                  {(() => {
                                    const role = (resolvedProfessional.role ?? "").toLowerCase();
                                    const isVeterinarian = role.includes("veterin");
                                    const isGroomer = role.includes("groomer");
                                    if (isVeterinarian) {
                                      return (
                                        <Badge variant="secondary" className="h-6 text-xs">
                                          {t("calendar:appointmentForm.veterinarian")}
                                        </Badge>
                                      );
                                    }
                                    if (isGroomer) {
                                      return (
                                        <Badge variant="secondary" className="h-6 text-xs">
                                          Groomer
                                        </Badge>
                                      );
                                    }
                                    return (
                                      <Badge variant="outline" className="h-6 text-xs">
                                        {t("calendar:professional")}
                                      </Badge>
                                    );
                                  })()}
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-muted-foreground">
                                {t("calendar:appointmentDetails.noProfessionalAssigned")}
                              </p>
                            </>
                          )}
                        </div>
                      </section>

                      {/* Paciente + Responsável */}
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <div className="flex items-center gap-2">
                          <CustomIcons name="paws" className="h-5 w-5 text-current" />
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.patient")}</p>
                        </div>
                        <div className="mt-3 space-y-4">
                          {pets.length > 1 ? (
                            <div className="inline-flex h-9 items-center justify-start rounded-full bg-muted/50 p-1 overflow-x-auto">
                              {pets.map((petItem) => {
                                const isActive = petItem.id === activePetId;
                                return (
                                  <button
                                    key={petItem.id}
                                    type="button"
                                    onClick={() => setActivePetId(petItem.id)}
                                    className={cn(
                                      "inline-flex items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-all",
                                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                      isActive
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "text-muted-foreground hover:bg-muted",
                                    )}
                                  >
                                    <span className="max-w-[180px] truncate">
                                      {petItem.name?.trim?.() ? petItem.name : t("calendar:appointmentForm.patient")}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                          {!activePetId ? (
                            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                              {t("calendar:appointmentDetails.selectPatientToView")}
                            </div>
                          ) : isLoadingPetSummary ? (
                            <div className="rounded-lg border border-dashed p-4">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                {t("calendar:appointmentDetails.loadingPatientSummary")}
                              </div>
                            </div>
                          ) : !pet ? (
                            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                              {t("calendar:appointmentDetails.patientLoadError")}
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={petPhoto ?? undefined} alt={pet.name} />
                                  <AvatarFallback className="bg-muted/50 text-muted-foreground">
                                    <CustomIcons name="paws" className="h-5 w-5 text-current" />
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <Link
                                    to={`/patients/pets/${activePetId}`}
                                    onClick={() => onOpenChange(false)}
                                    className="block truncate text-base font-semibold text-primary hover:underline"
                                  >
                                    {pet.name}
                                  </Link>
                                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                                    {[
                                      pet.species?.name,
                                      pet.breed?.name,
                                      pet.birth_date ? formatPetAge(pet.birth_date) : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                </div>
                              </div>

                              <div className="grid gap-2">
                                {allergies.length ? (
                                  <div>
                                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.allergies")}</p>
                                    <div className="mt-1">
                                      <div className="flex flex-wrap gap-1">
                                        {allergies.map((item) => (
                                          <Badge key={item.id} variant="default">
                                            {item.name}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : null}

                                {temperaments.length ? (
                                  <div>
                                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.temperament")}</p>
                                    <div className="mt-1">
                                      <div className="flex flex-wrap gap-1">
                                        {temperaments.map((item) => (
                                          <Badge key={item.id} variant="secondary">
                                            {item.name}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>

                              {petNotes ? (
                                <div className="rounded-md border bg-muted/30 p-3">
                                  <p className="text-xs font-semibold text-muted-foreground">
                                    {t("calendar:appointmentDetails.patientNotes")}
                                  </p>
                                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                                    {petNotes}
                                  </p>
                                </div>
                              ) : null}

                              <Separator />

                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <p className="text-sm font-semibold text-foreground">{t("calendar:quickStart.owner")}</p>
                                </div>

                                {appointment.owner_id && ownerName ? (
                                  <Link
                                    to={`/patients/owners/${appointment.owner_id}`}
                                    onClick={() => onOpenChange(false)}
                                    className="block text-base font-semibold text-primary hover:underline"
                                  >
                                    {ownerName}
                                  </Link>
                                ) : (
                                  <p className="text-sm text-muted-foreground">{t("calendar:appointmentDetails.noOwnerAssigned")}</p>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  {primaryPhone?.phone_number?.trim?.() ? (
                                    <div className="flex items-start gap-2">
                                      <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.phone")}</p>
                                        {primaryPhone.is_whatsapp ? (
                                          <WhatsAppPhoneLink
                                            phoneNumber={primaryPhone.phone_number}
                                            className="min-w-0"
                                            textClassName="truncate text-sm text-foreground hover:text-primary"
                                          />
                                        ) : (
                                          <p className="truncate text-sm text-foreground">
                                            {primaryPhone.phone_number}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}
                                  {primaryEmail?.email?.trim?.() ? (
                                    <div className="flex items-start gap-2">
                                      <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.email")}</p>
                                        <p className="truncate text-sm text-foreground">
                                          {primaryEmail.email}
                                        </p>
                                      </div>
                                    </div>
                                  ) : null}
                                  {ownerAddressLabel ? (
                                    <div className="flex items-start gap-2 md:col-span-2">
                                      <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.address")}</p>
                                        <AddressLink addressText={ownerAddressLabel}>
                                          <p className="text-sm">{ownerAddressLabel}</p>
                                        </AddressLink>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>

                                {ownerNotes ? (
                                  <div className="rounded-md border bg-muted/30 p-3">
                                    <p className="text-xs font-semibold text-muted-foreground">
                                      {t("calendar:appointmentDetails.ownerNotes")}
                                    </p>
                                    <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{ownerNotes}</p>
                                  </div>
                                ) : null}
                              </div>

                              <Separator />

                              <div className="space-y-3">
                                {canResumeAttendance && resumePetId ? (
                                  <PermissionGuard resource="medical_records" action="create" showAlert={false}>
                                    <Button asChild size="sm" className="gap-2 w-full">
                                      <Link
                                        to={`/patients/pets/${resumePetId}/medical-records/new`}
                                        state={{
                                          appointmentId: appointment.id,
                                          quickStartAttendance: true,
                                          quickStartSource,
                                        }}
                                        onClick={() => onOpenChange(false)}
                                      >
                                        <Play className="h-4 w-4" />
                                        {t("calendar:appointmentDetails.resumeAttendance")}
                                      </Link>
                                    </Button>
                                  </PermissionGuard>
                                ) : null}
                                {canOfferStartAttendance && resumePetId && appointment ? (
                                  <PermissionGuard resource="medical_records" action="create" showAlert={false}>
                                    <Button
                                      size="sm"
                                      className="gap-2 w-full"
                                      disabled={updateAppointmentStatus.isPending}
                                      onClick={() => {
                                        updateAppointmentStatus.mutate(
                                          { id: appointment.id, status: "em_atendimento" as AppointmentStatus },
                                          {
                                            onSuccess: () => {
                                              onOpenChange(false);
                                              navigate(`/patients/pets/${resumePetId}/medical-records/new`, {
                                                state: {
                                                  appointmentId: appointment.id,
                                                  quickStartAttendance: true,
                                                  quickStartSource,
                                                },
                                              });
                                            },
                                          },
                                        );
                                      }}
                                    >
                                      <Play className="h-4 w-4" />
                                      {t("calendar:appointmentDetails.startAttendance")}
                                    </Button>
                                  </PermissionGuard>
                                ) : null}
                              </div>
                            </>
                          )}
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-base font-semibold text-foreground">{t("calendar:appointmentDetails.appointmentNotFound")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("calendar:appointmentDetails.appointmentNotFoundDescription")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Nested dialogs (day events, edit) */}
          <RecentConsultationsCard
            records={selectedDayMedicalRecord ? [selectedDayMedicalRecord] : []}
            hideList
            dialogOpen={isDayMedicalRecordModalOpen}
            onDialogOpenChange={setIsDayMedicalRecordModalOpen}
            selectedRecordId={selectedDayMedicalRecord?.id ?? null}
            onSelectedRecordIdChange={(id) => {
              if (!id) setSelectedDayMedicalRecord(null);
            }}
          />

          <Dialog open={isDayVaccinationModalOpen} onOpenChange={setIsDayVaccinationModalOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{t("calendar:appointmentDetails.application")}</DialogTitle>
              </DialogHeader>
              {selectedDayVaccination ? (
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.application")}</p>
                    <p className="text-foreground">
                      {selectedDayVaccination.item_id?.name || t("calendar:appointmentDetails.application")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:type")}</p>
                    <p className="text-foreground">
                      {selectedDayVaccination.item_id?.product_subtype?.name?.toLowerCase() === "medicamento"
                        ? "Medicamento"
                        : t("calendar:appointmentForm.vaccine")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.applicationDate")}</p>
                    <p className="text-foreground">
                      {formatEventDateTime(selectedDayVaccination.application_date ?? selectedDayVaccination.created_at)}
                    </p>
                  </div>
                  {selectedDayVaccination.dose_number ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.dose")}</p>
                      <p className="text-foreground">{t("calendar:appointmentDetails.dose")} {selectedDayVaccination.dose_number}</p>
                    </div>
                  ) : null}
                  {selectedDayVaccination.batch_number ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.batch")}</p>
                      <p className="text-foreground">{selectedDayVaccination.batch_number}</p>
                    </div>
                  ) : null}
                  {selectedDayVaccination.next_dose_date && !selectedDayVaccination.next_dose_completed_at ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.nextDose")}</p>
                      <p className="text-foreground">{formatDateBR(selectedDayVaccination.next_dose_date)}</p>
                    </div>
                  ) : null}
                  {selectedDayVaccination.adverse_reaction ? (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                      <p className="text-xs font-semibold text-destructive">{t("calendar:appointmentDetails.adverseReaction")}</p>
                      <p className="mt-1 text-foreground">{selectedDayVaccination.adverse_reaction}</p>
                    </div>
                  ) : null}
                  {selectedDayVaccination.notes ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.observations")}</p>
                      <p className="whitespace-pre-wrap text-foreground">{selectedDayVaccination.notes}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </DialogContent>
          </Dialog>

          <Dialog open={isDayExamModalOpen} onOpenChange={setIsDayExamModalOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{t("calendar:appointmentDetails.document")}</DialogTitle>
              </DialogHeader>
              {selectedDayExam ? (
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.name")}</p>
                    <p className="text-foreground">
                      {selectedDayExam.file_name || selectedDayExam.original_name || selectedDayExam.type || t("calendar:appointmentDetails.document")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.date")}</p>
                    <p className="text-foreground">
                      {formatEventDateTime(selectedDayExam.exam_date ?? selectedDayExam.created_at)}
                    </p>
                  </div>
                  {selectedDayExam.description ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.description")}</p>
                      <p className="whitespace-pre-wrap text-foreground">{selectedDayExam.description}</p>
                    </div>
                  ) : null}
                  <div className="pt-1">
                    <Button type="button" onClick={() => void handleOpenExamFileFromModal(selectedDayExam)}>
                      {t("calendar:appointmentDetails.openDocumentFile")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </DialogContent>
          </Dialog>

          <Dialog open={isDayGroomingModalOpen} onOpenChange={setIsDayGroomingModalOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{t("calendar:appointmentDetails.grooming")}</DialogTitle>
              </DialogHeader>
              {selectedDayGrooming ? (
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.status")}</p>
                    <p className="text-foreground">
                      {getGroomingStatusLabel(selectedDayGrooming.grooming_status || selectedDayGrooming.status) || t("calendar:appointmentDetails.scheduled")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.date")}</p>
                    <p className="text-foreground">
                      {formatEventDateTime(selectedDayGrooming.starts_at ?? selectedDayGrooming.created_at)}
                    </p>
                  </div>
                  {selectedDayGrooming.appointment_items?.length ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.services")}</p>
                      <div className="mt-1 space-y-1">
                        {selectedDayGrooming.appointment_items.map((item, index) => {
                          const serviceName = item.title_snapshot || item.item?.name;
                          if (!serviceName) return null;
                          return (
                            <p key={`${serviceName}-${index}`} className="text-foreground">
                              • {serviceName}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {selectedDayGrooming.notes ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("calendar:appointmentDetails.observations")}</p>
                      <p className="whitespace-pre-wrap text-foreground">{selectedDayGrooming.notes}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </DialogContent>
          </Dialog>

          {selectedDayPrescription ? (
            <PrescriptionViewDialog
              open={isDayPrescriptionModalOpen}
              onOpenChange={setIsDayPrescriptionModalOpen}
              prescription={selectedDayPrescription}
            />
          ) : null}

          {/* Edit modal */}
          <Dialog
            open={isEditing}
            onOpenChange={(isOpen) => {
              if (!isOpen) handleCloseEdit();
            }}
          >
            <DialogContent noScrollViewport className="max-h-[90vh] max-w-5xl border border-border/60 bg-white p-0">
              {appointment && (
                <AppointmentFormBody
                  mode="edit"
                  clinicId={appointment.clinic_id}
                  appointmentId={appointment.id}
                  initialAppointment={appointment}
                  onClose={handleCloseEdit}
                  onSuccess={handleCloseEdit}
                  onDeleteSuccess={() => {
                    handleCloseEdit();
                    onOpenChange(false);
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={itemScopeDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!isApplyingItemScope) {
            setItemScopeDialogOpen(nextOpen);
            if (!nextOpen) setPendingItemAction(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:appointmentDetails.itemChangeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:appointmentDetails.itemChangeDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isApplyingItemScope}>{t("common:close")}</AlertDialogCancel>
            <AlertDialogAction disabled={isApplyingItemScope} onClick={() => void handleScopeAction("single")}>
              {t("calendar:appointmentForm.onlyThis")}
            </AlertDialogAction>
            <AlertDialogAction disabled={isApplyingItemScope} onClick={() => void handleScopeAction("future")}>
              {t("calendar:appointmentForm.thisAndNext")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelSaleDialogOpen} onOpenChange={setCancelSaleDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("erp:saleDetail.cancelSaleConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("erp:saleDetail.cancelSaleConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancellingSale}>
              {t("common:close")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isCancellingSale}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!saleByAppointment?.id) return;
                try {
                  await cancelLinkedSale({ saleId: saleByAppointment.id, status: "CANCELADA" });
                  setCancelSaleDialogOpen(false);
                } catch {
                  // updateSaleStatus already shows an error toast internally
                }
              }}
            >
              {isCancellingSale ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("erp:saleDetail.cancelSale")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
